// A bot's draft style (AI_MANAGERS.md §4.7): how a character in a seat
// drafts, beyond how well (`skill`, `ai.ts`).
//
// FOUR THINGS IT MIXES, read from its file (`@fsp/bots` `draft`): a plan for
// building its roster, its tastes in players, how it reacts to the room, and
// its skill, which is the seat's own. Every one of them is a number of PLACES:
// how far down the list a taste reaches for a player, or how far a dislike
// pushes one down. `pickAi` adds them to a player's place on the list and
// takes the lowest, so a strong taste takes a player a round early and a weak
// one only breaks a near tie, and the skill's noise stays on top of both.
//
// THE FLOOR IS NOT A TASTE. Whatever a bot likes, the last rounds fill the
// starters it still lacks, the caps hold, and a kicker waits for the end
// (`pickAi`'s first branch, and `bestAvailable` when its tastes leave nobody).
// A style bends its picks; it never leaves a lineup it cannot field.
//
// EACH LEAGUE IS A LITTLE DIFFERENT. The clubs a bot loves and hates, and the
// players it has its eye on, are drawn from the league's id (`styleSeed`) and
// the bot's name, so a bot drafts the same tastes in a league's mock as in its
// draft, and another league gets other ones. A club its file names is its own
// in every league that has it.
//
// IT NAMES NO SPORT. A position or a club comes from the file, under the
// sport's own key, and is compared with what the context says a player is.

import { castMember } from '@fsp/bots';
import { hashUnit } from './context';
import type { DraftContext, DraftState } from './engine';
import { ownerOf, totalPicks, holderOf } from './order';

/** What a bot can know about a player at a draft, besides his position and place on the list. */
export interface DraftFacts {
  /** His pro club's id, or null for a free agent. */
  club: string | null;
  bye: number | null;
  /** In his first season. */
  rookie: boolean;
  /** Seasons in the league, where known. */
  years: number | null;
  /** On the injury report: 0 not, 1 a warning, 2 likely out or out. */
  hurt: 0 | 1 | 2;
  /** Games he played last season, where he played. */
  games: number | null;
  first: string | null;
}

/** A pro club as a bot's style names it: "Vikings", and its division for a rivalry. */
export interface DraftClub {
  name: string;
  division: string | null;
}

/** A bot's style for one league's draft: its file, with the league's draws made. */
export interface DraftStyle {
  build: 'value' | 'starters-first';
  lateEarly: number;
  order: string[];
  targets: { position: string; atLeast: number; byRound: number; strength: number }[];
  limits: { position: string; atMost: number; throughRound: number }[];
  hoard: string | null;
  handcuff: string | null;
  stack: { with: string; positions: string[]; strength: number } | null;
  /** Club ids. */
  loves: string[];
  loveStrength: number;
  /** Club ids it will not draft from: its own and its loved clubs' rivals. */
  hates: string[];
  /** Its own grudges, without the rivals, for its words. */
  grudges: string[];
  division: boolean;
  rookies: 'love' | 'avoid' | null;
  veterans: boolean;
  injured: 'avoid' | 'bargain' | null;
  ironMan: boolean;
  namesake: string[];
  byes: 'spread' | 'stack' | null;
  runs: 'follow' | 'against' | null;
  forecast: boolean;
  hawk: boolean;
  reach: number;
  tilt: boolean;
  copycat: boolean;
  panic: boolean;
  drain: boolean;
  /** What its own draws are keyed on: the league and its name. */
  seed: string;
}

// ── How much each part is worth, in places ───────────────────────────────────

/** A backup while a starter is still missing, for a bot that fills its starters first. */
const STARTERS_FIRST = 12;
/** The first position in its order still short of starters; the next gets half. */
const ORDER_FIRST = 10;
/** A target's pull at full urgency, unless its file says. */
const TARGET = 10;
const HOARD = 5;
const HANDCUFF = 8;
/** A late position it needs, in the rounds it lets them in sooner than the habit. */
const LATE_EARLY = 6;
/** Its loved clubs: the full strength for its first three, half for two more, then none. */
const LOVED_FULL = 3;
const LOVED_HALF = 5;
const STACK = 6;
const ROOKIE = 7;
/** Seasons past which a veteran pleases it, a place a season, up to five. */
const VETERAN_FROM = 5;
const VETERAN_MOST = 5;
/** What the injury report costs a player, scaled by how little risk the bot takes. */
const HURT_WARN = 4;
const HURT_OUT = 12;
/** A hurt player to a bot that gambles on them, for its first few; then he is like anybody. */
const BARGAIN = 5;
const BARGAIN_MOST = 3;
const IRON_MAN = 4;
const NAMESAKE = 5;
const BYE_SPREAD = 4;
const BYE_STACK = 3;
/** A lucky bye week is a few players' week off, never the whole team's. */
const BYE_STACK_MOST = 3;
/** Its eye for a few players: a little for every bot, more for a reacher. */
const REACH = 3;
/** The share of the players likely to be drafted that a bot has its eye on. */
const GUY_SHARE = 0.06;
const RUN_FOLLOW = 5;
const RUN_AGAINST = 4;
const QUIET = 2;
const COPYCAT = 4;
const HAWK = 6;
/** The most a scarce position's top player gains, and how much of the drop it counts. */
const FORECAST_MOST = 12;
const FORECAST_SHARE = 0.6;
/** How likely a team picking before my next turn takes a position: one it must start, or not. */
const TAKES_NEEDED = 0.5;
const TAKES_OTHER = 0.12;

/**
 * A bot's draft style in this league, or null for one with none: a context
 * that names no sport (the simulator's season suite drafts so, as every
 * bot did before styles), or a character with no file.
 */
export function draftStyleOf(
  ai: { presetId: string; name: string },
  ctx: Pick<DraftContext, 'sport' | 'styleSeed' | 'clubs'>,
  fallbackSeed: string,
): DraftStyle | null {
  if (ctx.sport === undefined) return null;
  const file = castMember(ai.presetId)?.draft;
  if (!file) return null;
  const plan = file.sports[ctx.sport] ?? {};
  const seed = `${ctx.styleSeed ?? fallbackSeed}|${ai.name}`;
  const clubs = Object.keys(ctx.clubs ?? {}).sort();
  const drawn = (what: string, count: number, named: readonly string[], not: readonly string[]) => {
    const own = named.filter((c) => clubs.includes(c) && !not.includes(c)).slice(0, count);
    const rest = clubs
      .filter((c) => !own.includes(c) && !not.includes(c))
      .map((c) => ({ c, at: hashUnit(`${seed}|${what}|${c}`) }))
      .sort((a, b) => a.at - b.at || (a.c < b.c ? -1 : 1))
      .map((x) => x.c);
    return [...own, ...rest].slice(0, count);
  };
  const t = file.tastes;
  const loves = t.loves ? drawn('loves', t.loves.count, plan.loves ?? [], []) : [];
  const grudges = t.hates ? drawn('hates', t.hates.count, plan.hates ?? [], loves) : [];
  // ITS LOVED CLUBS' RIVALS, for a bot that will not draft from them either.
  const rivals =
    t.division === true
      ? clubs.filter((c) => {
          const division = ctx.clubs?.[c]?.division ?? null;
          return (
            !loves.includes(c) &&
            division !== null &&
            loves.some((l) => ctx.clubs?.[l]?.division === division)
          );
        })
      : [];
  return {
    build: file.build,
    lateEarly: file.lateEarly ?? 0,
    order: plan.order ?? [],
    targets: (plan.targets ?? []).map((x) => ({ ...x, strength: x.strength ?? TARGET })),
    limits: plan.limits ?? [],
    hoard: plan.hoard ?? null,
    handcuff: plan.handcuff ?? null,
    stack: plan.stack ? { ...plan.stack, strength: plan.stack.strength ?? STACK } : null,
    loves,
    loveStrength: t.loves?.strength ?? 0,
    hates: [...new Set([...grudges, ...rivals])],
    grudges,
    division: rivals.length > 0,
    rookies: t.rookies ?? null,
    veterans: t.veterans === true,
    injured: t.injured ?? null,
    ironMan: t.ironMan === true,
    namesake: (t.namesake ?? []).map((n) => n.toLowerCase()),
    byes: t.byes ?? null,
    runs: file.room.runs ?? null,
    forecast: file.room.forecast === true,
    hawk: file.room.hawk === true,
    reach: file.room.reach ?? REACH,
    tilt: file.room.tilt === true,
    copycat: file.room.copycat === true,
    panic: file.room.panic === true,
    drain: file.room.drain === true,
    seed,
  };
}

// ── A look at the team and the room, once a pick ─────────────────────────────

/** What a style reads about a team and the room, worked out once for every player it weighs. */
export interface StyleLook {
  /** The team's own round: the players it has, plus one. */
  round: number;
  have: Record<string, number>;
  /** The clubs of its players, by position. */
  clubsAt: Map<string, Map<string, number>>;
  /** Bye weeks of its players, by position, and all of them but the late positions'. */
  byesAt: Map<string, Set<number>>;
  /** How many of its players, the late positions aside, are off each bye week. */
  byes: Map<number, number>;
  lovedTaken: number;
  hurtTaken: number;
  /** A starting position still short, not counting the late ones. */
  missingStarter: boolean;
  /** The positions of its order still short of starters, in its order. */
  orderShort: string[];
  /** Whether the floor still keeps the late positions back, and whether its own habit does. */
  floorSavesLate: boolean;
  styleSavesLate: boolean;
  /** What the room just did: positions of the last picks, newest last. */
  recent: string[];
  /** Picks before this team's next turn; null in an auction. */
  untilNext: number | null;
  panicking: boolean;
  /** The bonus for each position's top player, for a bot that counts what will run out. */
  scarce: Map<string, { id: string; places: number }>;
  guys: Set<string>;
  /** Each player's place on the house list, for a bot that pounces on a fallen one. */
  house: Map<string, number> | null;
  /** The most games anybody played last season, for the bot that wants every-week players. */
  mostGames: number;
  risk: number;
  /** The pick being made, 1-based; for an auction, the sales so far plus one. */
  overall: number;
  teams: number;
}

/** The picks after `overall` before `teamId` picks again, or the rest of the draft. */
function picksUntilNext(state: DraftState, teamId: string, overall: number): number {
  const total = totalPicks(state);
  for (let o = overall + 1; o <= total; o += 1) {
    if (ownerOf(state, o) === teamId) return o - overall - 1;
  }
  return total - overall;
}

/**
 * The look a style takes before weighing players. `roster` is what the team
 * has (in an auction, with the players it is ahead on); a pick draft passes
 * none and the team's own is read.
 */
export function styleLook(
  state: DraftState,
  teamId: string,
  ctx: DraftContext,
  style: DraftStyle,
  input: { roster: readonly string[]; auction: boolean; risk: number },
): StyleLook {
  const { roster } = input;
  const have: Record<string, number> = {};
  const clubsAt = new Map<string, Map<string, number>>();
  const byesAt = new Map<string, Set<number>>();
  const byes = new Map<number, number>();
  let lovedTaken = 0;
  let hurtTaken = 0;
  for (const id of roster) {
    const pos = ctx.positionOf(id);
    have[pos] = (have[pos] ?? 0) + 1;
    const f = ctx.facts?.[id];
    if (f && f.hurt > 0) hurtTaken += 1;
    if (f?.club) {
      const at = clubsAt.get(pos) ?? new Map<string, number>();
      at.set(f.club, (at.get(f.club) ?? 0) + 1);
      clubsAt.set(pos, at);
      if (style.loves.includes(f.club)) lovedTaken += 1;
    }
    if (f?.bye != null) {
      const at = byesAt.get(pos) ?? new Set<number>();
      at.add(f.bye);
      byesAt.set(pos, at);
      if (!ctx.latePositions.includes(pos)) byes.set(f.bye, (byes.get(f.bye) ?? 0) + 1);
    }
  }
  const short = (pos: string) => (have[pos] ?? 0) < (ctx.starterNeeds[pos] ?? 0);
  const missingStarter = Object.keys(ctx.starterNeeds).some(
    (pos) => !ctx.latePositions.includes(pos) && short(pos),
  );
  const round = roster.length + 1;
  const lateNeeded = ctx.latePositions.filter(short).length;
  const roundsLeft = state.rounds - roster.length;

  const overall = state.picks.length + 1;
  const teams = state.order.length;
  const untilNext = input.auction ? null : picksUntilNext(state, teamId, overall);
  const recent = input.auction ? [] : state.picks.slice(-10).map((p) => ctx.positionOf(p.playerId));

  const taken = new Set(state.picks.map((p) => p.playerId));
  for (const lot of state.auction?.lots ?? []) taken.add(lot.playerId);
  // Keepers still to be placed are nobody's to draft (§2.3).
  for (const k of state.keepers ?? []) taken.add(k.playerId);

  // THE PLAYERS IT HAS ITS EYE ON: a few of those likely to be drafted, drawn
  // for this league and this bot. Everybody has a few; a reacher wants his more.
  const likely = ctx.rankings.slice(0, Math.max(1, Math.round(teams * state.rounds * 0.7)));
  const guys = new Set(likely.filter((id) => hashUnit(`${style.seed}|guy|${id}`) < GUY_SHARE));

  let house: Map<string, number> | null = null;
  if (style.hawk && !input.auction) house = new Map(ctx.rankings.map((id, i) => [id, i]));

  const scarce = new Map<string, { id: string; places: number }>();
  if (style.forecast && untilNext !== null && ctx.values) {
    const values = ctx.values;
    const left = ctx.rankings.filter((id) => !taken.has(id));
    const top = left.slice(0, 31).map((id) => values[id] ?? 0);
    const span = Math.min(30, top.length - 1);
    const gap = span > 0 ? (top[0]! - top[span]!) / span : 0;
    if (gap > 0) {
      // WHAT EACH TEAM PICKING BEFORE MY NEXT TURN IS LIKELY TO TAKE: a
      // position it must still start, mostly, and now and then any other.
      const shortOf = new Map<string, Record<string, number>>();
      const expected: Record<string, number> = {};
      for (let o = overall + 1; o <= overall + untilNext; o += 1) {
        const team = ownerOf(state, o);
        let counts = shortOf.get(team);
        if (!counts) {
          counts = {};
          for (const p of state.picks) {
            if (holderOf(state, p) !== team) continue;
            const pos = ctx.positionOf(p.playerId);
            counts[pos] = (counts[pos] ?? 0) + 1;
          }
          shortOf.set(team, counts);
        }
        for (const pos of Object.keys(ctx.starterNeeds)) {
          if (ctx.latePositions.includes(pos)) continue;
          const needed = (counts[pos] ?? 0) < (ctx.starterNeeds[pos] ?? 0);
          expected[pos] = (expected[pos] ?? 0) + (needed ? TAKES_NEEDED : TAKES_OTHER);
        }
      }
      for (const [pos, e] of Object.entries(expected)) {
        const at = left.filter((id) => ctx.positionOf(id) === pos).slice(0, 12);
        if (at.length === 0) continue;
        const now = values[at[0]!] ?? 0;
        const later = values[at[Math.round(e)] ?? ''] ?? 0;
        const places = Math.min(FORECAST_MOST, (FORECAST_SHARE * (now - later)) / gap);
        if (places > 0) scarce.set(pos, { id: at[0]!, places });
      }
    }
  }

  let mostGames = 0;
  if (style.ironMan) {
    for (const f of Object.values(ctx.facts ?? {})) mostGames = Math.max(mostGames, f.games ?? 0);
  }

  return {
    round,
    have,
    clubsAt,
    byesAt,
    byes,
    lovedTaken,
    hurtTaken,
    missingStarter,
    orderShort: style.order.filter(short),
    floorSavesLate: roundsLeft > lateNeeded,
    styleSavesLate: roundsLeft > lateNeeded + style.lateEarly,
    recent,
    untilNext,
    panicking: style.panic && untilNext !== null && untilNext >= 1.5 * teams,
    scarce,
    guys,
    house,
    mostGames,
    risk: input.risk,
    overall,
    teams,
  };
}

// ── Weighing a player ─────────────────────────────────────────────────────

/** A player this bot will not take now, whatever his place: a grudge, a limit, a dislike. */
export function styleRefuses(
  style: DraftStyle,
  look: StyleLook,
  id: string,
  ctx: DraftContext,
): boolean {
  const f = ctx.facts?.[id];
  if (f?.club && style.hates.includes(f.club)) return true;
  if (f?.rookie && style.rookies === 'avoid') return true;
  if (f && f.hurt > 0 && style.injured === 'avoid') return true;
  const pos = ctx.positionOf(id);
  return style.limits.some(
    (l) => l.position === pos && look.round <= l.throughRound && (look.have[pos] ?? 0) >= l.atMost,
  );
}

/**
 * How many places its plan and its tastes move a player up (down, below
 * zero): what it would weigh at any table, the room aside. An auction prices
 * by this; a pick draft adds the room's (`roomPull`).
 */
export function stylePull(
  style: DraftStyle,
  look: StyleLook,
  id: string,
  ctx: DraftContext,
): number {
  const pos = ctx.positionOf(id);
  const f = ctx.facts?.[id];
  const has = look.have[pos] ?? 0;
  const starts = ctx.starterNeeds[pos] ?? 0;
  const late = ctx.latePositions.includes(pos);
  let places = 0;

  // ── The plan
  if (style.build === 'starters-first' && look.missingStarter && !late && has >= starts) {
    places -= STARTERS_FIRST;
  }
  const inOrder = look.orderShort.indexOf(pos);
  if (inOrder === 0) places += ORDER_FIRST;
  else if (inOrder === 1) places += ORDER_FIRST / 2;
  for (const target of style.targets) {
    if (target.position !== pos || has >= target.atLeast || look.round > target.byRound) continue;
    const urgency = (target.atLeast - has) / (target.byRound - look.round + 1);
    places += target.strength * Math.min(1.5, urgency);
  }
  if (style.hoard === pos && has >= starts) places += HOARD;
  if (style.handcuff === pos && has >= starts && f?.club) {
    const mates = look.clubsAt.get(pos)?.get(f.club) ?? 0;
    if (mates === 1) places += HANDCUFF;
  }
  if (late && look.floorSavesLate && !look.styleSavesLate && has < starts) places += LATE_EARLY;

  // ── Its tastes
  if (f?.club && style.loves.includes(f.club)) {
    const share = look.lovedTaken < LOVED_FULL ? 1 : look.lovedTaken < LOVED_HALF ? 0.5 : 0;
    places += style.loveStrength * share;
  }
  if (style.stack && f?.club) {
    if (style.stack.positions.includes(pos) && look.clubsAt.get(style.stack.with)?.has(f.club)) {
      places += style.stack.strength;
    } else if (
      pos === style.stack.with &&
      style.stack.positions.some((p) => look.clubsAt.get(p)?.has(f.club!))
    ) {
      places += style.stack.strength / 2;
    }
  }
  if (f?.rookie && style.rookies === 'love') places += ROOKIE;
  if (style.veterans && f?.years != null && f.years > VETERAN_FROM) {
    places += Math.min(VETERAN_MOST, f.years - VETERAN_FROM);
  }
  if (f && f.hurt > 0) {
    if (style.injured === 'bargain') {
      if (look.hurtTaken < BARGAIN_MOST) places += BARGAIN;
    }
    // EVERY BOT IS WARY OF THE INJURY REPORT, as much as its risk says.
    else places -= (1 - look.risk) * (f.hurt === 2 ? HURT_OUT : HURT_WARN);
  }
  if (style.ironMan && f?.games != null && look.mostGames > 0) {
    if (f.games >= look.mostGames - 2) places += IRON_MAN;
    else if (f.games < look.mostGames / 2) places -= IRON_MAN;
  }
  if (f?.first && style.namesake.includes(f.first.toLowerCase())) places += NAMESAKE;
  if (f?.bye != null && style.byes === 'spread' && look.byesAt.get(pos)?.has(f.bye)) {
    places -= BYE_SPREAD;
  }
  if (f?.bye != null && style.byes === 'stack' && !late) {
    const off = look.byes.get(f.bye) ?? 0;
    if (off > 0 && off < BYE_STACK_MOST) places += BYE_STACK;
  }
  if (look.guys.has(id)) places += style.reach;
  return places;
}

/** How many places what the room is doing moves a player, in a pick draft. */
export function roomPull(
  style: DraftStyle,
  look: StyleLook,
  id: string,
  ctx: DraftContext,
): number {
  const pos = ctx.positionOf(id);
  const late = ctx.latePositions.includes(pos);
  let places = 0;
  const lastSix = look.recent.slice(-6);
  const inRun = lastSix.filter((p) => p === pos).length;
  if (style.runs === 'follow' && inRun >= 3) places += RUN_FOLLOW;
  if (style.runs === 'against') {
    if (inRun >= 2) places -= RUN_AGAINST;
    else if (!late && look.recent.length >= 10 && !look.recent.includes(pos)) places += QUIET;
  }
  if (style.copycat && look.recent.length > 0 && look.recent[look.recent.length - 1] === pos) {
    places += COPYCAT;
  }
  const scarce = look.scarce.get(pos);
  if (scarce?.id === id) places += scarce.places;
  if (look.house) {
    const fall = look.overall - 1 - (look.house.get(id) ?? look.overall);
    if (fall >= look.teams / 2) places += HAWK * Math.min(1, fall / (2 * look.teams));
  }
  return places;
}

// ── Words ─────────────────────────────────────────────────────────────────

const COUNT_WORDS = ['', 'One', 'Two', 'Three', 'Four'];

/** "Vikings", or the club's id when the league's list has no name for it. */
function clubName(id: string, ctx: Pick<DraftContext, 'clubs'>): string {
  return ctx.clubs?.[id]?.name ?? id;
}

function andList(words: readonly string[]): string {
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]!}`;
}

/**
 * A bot's style in a few short phrases, its plan first, then its tastes, then
 * the room: what its seat says about it ("Starters before backups · Loves the
 * Vikings · No Packers"). Words, no numbers, so every detail level reads the
 * same. In an auction a plan by rounds reads by what it buys first.
 */
export function styleWords(
  style: DraftStyle,
  ctx: Pick<DraftContext, 'clubs' | 'latePositions'>,
  auction: boolean,
): string[] {
  const out: string[] = [];
  if (style.build === 'starters-first') out.push('Starters before backups');
  if (style.order.length > 0) out.push(`${style.order.join(', ')}, in that order`);
  for (const t of style.targets) {
    // ONE PHRASE FOR ONE IDEA: a target its limit already says ("One RB
    // early, then waits") is not said twice.
    if (style.limits.some((l) => l.position === t.position && l.atMost === t.atLeast)) continue;
    out.push(
      t.atLeast >= 2
        ? `${COUNT_WORDS[t.atLeast] ?? String(t.atLeast)} ${t.position}s early`
        : `${t.position} early`,
    );
  }
  for (const l of style.limits) {
    out.push(
      l.atMost === 0
        ? l.throughRound >= 6
          ? `${l.position} late`
          : `No early ${l.position}s`
        : `${COUNT_WORDS[l.atMost] ?? String(l.atMost)} ${l.position} early, then waits`,
    );
  }
  if (style.hoard) out.push(`Stocks up on ${style.hoard}s`);
  if (style.handcuff) out.push(`Backs up its own ${style.handcuff}s`);
  if (style.stack)
    out.push(
      `${style.stack.with} with his own ${andList(style.stack.positions.map((p) => `${p}s`))}`,
    );
  if (style.lateEarly > 0 && ctx.latePositions.length > 0) {
    out.push(`${andList([...ctx.latePositions])} early`);
  }
  if (out.length === 0) out.push('Best available');

  if (style.loves.length > 0) {
    out.push(`Loves the ${andList(style.loves.map((c) => clubName(c, ctx)))}`);
  }
  if (style.grudges.length > 0)
    out.push(`No ${andList(style.grudges.map((c) => clubName(c, ctx)))}`);
  if (style.division) out.push('No division rivals');
  if (style.rookies === 'love') out.push('Loves rookies');
  if (style.rookies === 'avoid') out.push('No rookies');
  if (style.veterans) out.push('Trusts veterans');
  if (style.injured === 'avoid') out.push('Steers clear of the injury report');
  if (style.injured === 'bargain') out.push('Gambles on the injured');
  if (style.ironMan) out.push('Wants every-week players');
  if (style.namesake.length > 0) {
    const name = style.namesake[0]!;
    out.push(`Soft spot for a ${name.charAt(0).toUpperCase()}${name.slice(1)}`);
  }
  if (style.byes === 'spread') out.push('Spreads its bye weeks');
  if (style.byes === 'stack') out.push('A lucky bye week');

  if (!auction) {
    if (style.runs === 'follow') out.push('Joins a run');
    if (style.runs === 'against') out.push('Goes where the room is not');
    if (style.forecast) out.push('Watches what will run out');
    if (style.hawk) out.push('Pounces on a fallen player');
    if (style.copycat) out.push('Copies the pick before');
    if (style.tilt) out.push('Tilts when sniped');
    if (style.panic) out.push('Nervous at the turn');
  }
  if (style.reach > REACH) out.push('Reaches for its guys');
  if (auction && style.drain) out.push('Puts up players to spend your money');
  return out;
}

/**
 * What its style came to, for the recap, as what follows its name: "took 4
 * Vikings, and no Packers", "drafted no Packers". Null when nothing it likes
 * showed. Only what can be counted from the picks.
 */
export function styleRecap(
  style: DraftStyle,
  playerIds: readonly string[],
  ctx: Pick<DraftContext, 'clubs' | 'facts'>,
): string | null {
  const took: string[] = [];
  const clubOf = (id: string) => ctx.facts?.[id]?.club ?? null;
  // EACH LOVED CLUB COUNTED ON ITS OWN, and only one it took two or more of.
  for (const club of style.loves) {
    const loved = playerIds.filter((id) => clubOf(id) === club).length;
    if (loved >= 2) took.push(`${String(loved)} ${clubName(club, ctx)}`);
  }
  if (style.rookies === 'love') {
    const rookies = playerIds.filter((id) => ctx.facts?.[id]?.rookie === true).length;
    if (rookies >= 2) took.push(`${String(rookies)} rookies`);
  }
  const spared =
    style.grudges.length > 0 &&
    playerIds.length > 0 &&
    playerIds.every((id) => !style.grudges.includes(clubOf(id) ?? ''));
  const none = spared ? `no ${andList(style.grudges.map((c) => clubName(c, ctx)))}` : null;
  if (took.length === 0) return none === null ? null : `drafted ${none}`;
  return none === null ? `took ${andList(took)}` : `took ${andList(took)}, and ${none}`;
}
